import { type DragEvent, type FC, useCallback, useState } from 'react';
import { Button, UploadIcon, Flex, Input, Text, Upload, UploadDragger } from '@/uicomponents';
import { Show } from '@/components/shared/show';
import { MAX_BRIEF_FILES, MAX_BRIEF_TOTAL_SIZE_BYTES } from '@/lib/utils/file/brief-file-gates';
import { formatFileSize } from '@/lib/utils';
import type { StagedBriefFile } from '@/hooks';
import { StagedFilePillList } from './staged-file-pill-list';
import styles from './brief-composer.module.css';

const { TextArea } = Input;

const ACCEPTED_EXTENSIONS = '.xlsx,.xls,.pptx,.ppt,.docx,.doc,.pdf,.txt,.jpg,.jpeg,.png';

// `accept` as an OBJECT, not the plain extension string, on the dragger only
// (fix round 2, NPJB-369). Verified against `@rc-component/upload`'s
// `AjaxUploader.filterFile`: the drop path always calls `filterFile(file,
// true)` (force=true). With a plain-string `accept`, that `force` makes
// rc-upload apply its OWN pre-filter against the extension list *before*
// `beforeUpload` ever runs, so a dropped `.zip`/`.csv` is silently discarded
// and our `UNSUPPORTED_FILE_TYPE_MESSAGE` never fires. `filter: 'native'`
// sets rc-upload's internal `filterFn` to `() => true`, which short-circuits
// that pre-filter regardless of `force` — the file reaches `beforeUpload`
// and our own gates (`applyBriefFileGates`) produce the message instead.
// `format` still carries the extension string, so the OS file-picker dialog
// (click path) filters exactly as before — that path was never affected: its
// `onChange` handler calls `filterFile` with `force=false`, which resolves to
// `() => true` either way (string or object `accept`), so `beforeUpload` (and
// our gates) already saw every clicked file regardless of this prop's shape.
const DRAGGER_ACCEPT = { format: ACCEPTED_EXTENSIONS, filter: 'native' as const };

const ERROR_MESSAGE_ID = 'brief-composer-error';

export interface BriefComposerProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  files: StagedBriefFile[];
  onAddFiles: (files: File[]) => void;
  onRemoveFile: (id: string) => void;
  maxChars: number;
  disabled?: boolean;
  error?: string | null;
  /**
   * Fires when a pick or drop is attempted while `disabled` is true (NPJB-369
   * fix round 2) — e.g. a parse is already in flight. The composer itself
   * can't show the "still working" notice (it owns no app state), so it
   * reports the attempt upward for `AudienceBrief` to surface it.
   */
  onAttemptWhileDisabled?: () => void;
}

/**
 * Presentational-only "one input, paperclip inside" composite (NPJB-369,
 * Variant E): the textarea, staged file pills, and the paperclip/counters
 * toolbar all live inside one bordered container, because typed words and
 * files go to the model as a single submission. No store access, no
 * mutations, no fetching — the parent owns state and wires this up.
 */
export const BriefComposer: FC<BriefComposerProps> = ({
  value,
  onChange,
  placeholder,
  files,
  onAddFiles,
  onRemoveFile,
  maxChars,
  disabled = false,
  error = null,
  onAttemptWhileDisabled,
}) => {
  // Own drag-over highlighting ourselves rather than trusting antd's
  // `-drag-hover` class (see brief-composer.module.css) — a depth counter
  // rather than a plain boolean, because `dragleave` fires when the pointer
  // crosses into a nested child (textarea, pills, toolbar), which would
  // otherwise flicker the highlight off and on as it moves around inside.
  const [dragDepth, setDragDepth] = useState(0);
  const isDragging = dragDepth > 0;

  const handleDragEnter = useCallback(
    (e: DragEvent) => {
      if (disabled) return;
      e.preventDefault();
      setDragDepth((depth) => depth + 1);
    },
    [disabled],
  );

  const handleDragOver = useCallback(
    (e: DragEvent) => {
      // Required or the browser never fires `drop`. Skipped while disabled
      // so the OS shows its native "not allowed" cursor instead of ours.
      if (disabled) return;
      e.preventDefault();
    },
    [disabled],
  );

  const handleDragLeave = useCallback(
    (e: DragEvent) => {
      if (disabled) return;
      e.preventDefault();
      setDragDepth((depth) => Math.max(0, depth - 1));
    },
    [disabled],
  );

  const handleDragDrop = useCallback(
    (e: DragEvent) => {
      setDragDepth(0);
      // `antd`'s own drop handler on the ancestor `.ant-upload-drag` (which
      // this bubbles to) already calls `preventDefault` — except while
      // `disabled`, when antd strips its handlers entirely, so we still
      // need to stop the browser's default "navigate to the dropped file".
      if (disabled) {
        e.preventDefault();
        // The reachable "attempt while locked" path (fix round 2): antd's
        // own drop handling is stripped while disabled, so nothing else here
        // would ever tell the parent a drop was attempted mid-parse.
        onAttemptWhileDisabled?.();
      }
    },
    [disabled, onAttemptWhileDisabled],
  );

  // Defensive counterpart for a click landing on the composer while disabled.
  // In practice every *interactive* descendant (textarea, Attach button,
  // remove buttons) is itself `disabled`, and disabled form controls never
  // dispatch `click` at all — so this only ever fires for a click on the
  // container's own surface (e.g. its padding), not a real file pick. It's
  // kept anyway as a harmless superset: it costs nothing and means a stray
  // click while locked still surfaces the same notice as a locked drop.
  const handleSurfaceClick = useCallback(() => {
    if (disabled) onAttemptWhileDisabled?.();
  }, [disabled, onAttemptWhileDisabled]);

  // antd calls `beforeUpload` once per file in the batch, but passes the full
  // batch as `fileList` on every call — handle the batch once, on the first
  // file, so `onAddFiles` sees it as a single pick (gate counting needs the
  // whole batch, not one file at a time).
  const handleBeforeUpload = useCallback(
    (file: File, fileList: File[]): false => {
      if (file === fileList[0]) onAddFiles(fileList);
      return false;
    },
    [onAddFiles],
  );

  const surfaceClassName = [styles.surface, isDragging ? styles.dragging : null]
    .filter(Boolean)
    .join(' ');

  const totalFileBytes = files.reduce((sum, staged) => sum + staged.file.size, 0);
  const hasError = error !== null;
  const errorMessageId = hasError ? ERROR_MESSAGE_ID : undefined;
  const fileCounterLabel = `${files.length} of ${MAX_BRIEF_FILES} files attached, using ${formatFileSize(totalFileBytes)} of ${formatFileSize(MAX_BRIEF_TOTAL_SIZE_BYTES)} allowed`;
  const charCounterLabel = `${value.length} of ${maxChars} characters used`;

  return (
    <UploadDragger
      hasControlInside
      openFileDialogOnClick={false}
      multiple
      showUploadList={false}
      beforeUpload={handleBeforeUpload}
      disabled={disabled}
      accept={DRAGGER_ACCEPT}
      className={styles.dropzone}
    >
      {/* antd's `pickAttrs` routes any `aria-*`/`data-*` prop given to
          UploadDragger onto its hidden `display:none` file input, not the
          visible drop surface — an aria-label up there is never announced.
          `role='group'` + `aria-label` here label the surface that's
          actually visible and actually receives the drop. */}
      <Flex
        vertical
        gap={8}
        role='group'
        aria-label='Attach files to your brief by dropping them here or using the Attach button below'
        className={surfaceClassName}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDragDrop}
        onClick={handleSurfaceClick}
      >
        <TextArea
          variant='borderless'
          classNames={{ textarea: styles.flush }}
          maxLength={maxChars}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          autoSize={{ minRows: 2, maxRows: 10 }}
          aria-invalid={hasError}
          aria-describedby={errorMessageId}
        />
        <StagedFilePillList files={files} onRemoveFile={onRemoveFile} disabled={disabled} />
        <Flex align='center' justify='space-between' className={styles.toolbar}>
          <Upload
            multiple
            showUploadList={false}
            beforeUpload={handleBeforeUpload}
            disabled={disabled}
            accept={ACCEPTED_EXTENSIONS}
          >
            <Button
              type='text'
              size='small'
              icon={<UploadIcon />}
              className={styles.flush}
              disabled={disabled}
              aria-label='Attach files'
            >
              Attach
            </Button>
          </Upload>
          <Flex gap={12}>
            <Show when={files.length > 0}>
              <Text variant='caption' tone='tertiary' aria-label={fileCounterLabel}>
                {files.length} of {MAX_BRIEF_FILES} · {formatFileSize(totalFileBytes)} /{' '}
                {formatFileSize(MAX_BRIEF_TOTAL_SIZE_BYTES)}
              </Text>
            </Show>
            <Text variant='caption' tone='tertiary' aria-label={charCounterLabel}>
              {value.length.toLocaleString('en-US')} / {maxChars.toLocaleString('en-US')} characters
            </Text>
          </Flex>
        </Flex>
        <Show when={hasError}>
          <Text variant='caption' tone='error' role='alert' id={ERROR_MESSAGE_ID}>
            {error}
          </Text>
        </Show>
      </Flex>
    </UploadDragger>
  );
};
